import { UploadProps } from "@commonlibs";
import { useState } from "react";
import { UploadedFile } from "../UploadInput.types";
import { UseUploadInputParams, UseUploadInputResult } from "./useUploadInput.types";

export default function useUploadInput({
    isLoading: isLoadingProp = false,
    readonly,
    disabled,
    onChange,
    upload,
    onError,
    onSuccess,
    onUploadStart,
    onUploadEnd,
    onFileUploaded,
}: UseUploadInputParams): UseUploadInputResult {
    const [isUploading, setIsUploading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [fileUri, setFileUri] = useState<UploadedFile | undefined>(undefined);

    const isLoading = isUploading || isLoadingProp;
    const isEditable = !isLoading && !readonly && !disabled;

    async function sendFileToServer(f: File): Promise<string | undefined> {
        try {
            //The existence is checked by the handleFileChange function
            return await upload!(f); // skipcq: JS-0339
        } catch (error) {
            setIsError(true);
            onError?.(error);
        }
    }

    async function handleSingleFileUpload(f: File) {
        onUploadStart?.();
        const uri = await sendFileToServer(f);
        if (uri) {
            setFileUri(uri);
            setIsSuccess(true);
            onSuccess?.(uri);
        }
        onUploadEnd?.(uri);
    }

    async function handleMultipleFilesUpload(files: FileList) {
        onUploadStart?.();
        const uris: string[] = [];
        for (const file of Array.from(files)) {
            const uri = await sendFileToServer(file);
            if (uri) {
                uris.push(uri);
                onFileUploaded?.(uri);
            }
        }
        setFileUri(uris);
        if (uris.length === files.length) {
            setIsSuccess(true);
            onSuccess?.(uris);
        }
        onUploadEnd?.(uris);
    }

    async function handleFileChange(f: UploadProps["value"]) {
        if (isLoading || !f) return;

        setIsUploading(true);
        setIsSuccess(false);
        setIsError(false);

        onChange?.(f);

        if (f instanceof File) {
            await handleSingleFileUpload(f);
        } else {
            await handleMultipleFilesUpload(f);
        }

        setIsUploading(false);
    }

    return { handleFileChange, isUploading, isEditable, isError, isSuccess, isLoading, fileUri };
}
